/** Обёртка над Telegram.WebApp.
 *
 * `initDataUnsafe` здесь используется ТОЛЬКО для оформления (имя, тема). Всё,
 * что влияет на данные, сервер берёт из подписанной `initData` — иначе любой
 * подставил бы чужой id прямо в консоли браузера.
 */
import { topInset } from "./safearea.ts";

type WebApp = {
  initData: string;
  colorScheme: "light" | "dark";
  viewportStableHeight?: number;
  ready: () => void;
  expand: () => void;
  //: Величины выреза — Bot API 8.0. У клиентов постарше их нет, и это не ноль,
  //: а молчание: см. `topInset`.
  safeAreaInset?: { top: number };
  contentSafeAreaInset?: { top: number };
  onEvent?: (name: string, cb: () => void) => void;
  HapticFeedback?: { impactOccurred: (s: "light" | "medium" | "heavy") => void };
  //: Значок на главном экране — Bot API 8.0. У старых клиентов методов просто
  //: нет, поэтому все обращения через `?.`, а не через проверку версии: версию
  //: пришлось бы сверять строкой, а отсутствие метода видно и так.
  addToHomeScreen?: () => void;
  checkHomeScreenStatus?: (cb: (status: HomeScreenStatus) => void) => void;
};

/** `unsupported` — клиент старый или платформа не умеет; `unknown` — умеет, но
 *  сказать, стоит ли значок, не может (так ведёт себя часть устройств). */
export type HomeScreenStatus = "unsupported" | "unknown" | "added" | "missed";

const tg: WebApp | undefined = (window as unknown as { Telegram?: { WebApp: WebApp } })
  .Telegram?.WebApp;

/** Кладём верхний отступ в переменную, из которой его берёт `--screen`.
 *
 *  Величины меняются на повороте экрана и при переходе в полноэкранный режим,
 *  поэтому это не разовый расчёт на старте, а подписка. `null` — клиент величин
 *  не знает: переменную НЕ трогаем, и CSS остаётся на своём `env()`.
 */
function applyTopInset(): void {
  const top = topInset(tg?.safeAreaInset, tg?.contentSafeAreaInset);
  if (top === null) return;
  document.documentElement.style.setProperty("--top-inset", `${top}px`);
}

export function initTelegram(): void {
  if (!tg) return; // открыли в обычном браузере — приложение всё равно должно жить
  tg.ready();
  tg.expand();
  applyTopInset();
  //: Три события, а не одно: вырез меняется на повороте, панель клиента — при
  //: входе в полный экран, а часть клиентов сообщает только viewportChanged.
  for (const name of ["safeAreaChanged", "contentSafeAreaChanged", "viewportChanged"]) {
    tg.onEvent?.(name, applyTopInset);
  }
  if (tg.viewportStableHeight) {
    document.documentElement.style.setProperty("--tg-safe-bottom", "16px");
  }
}

/** Подписанная строка для заголовка. Пустая — значит открыли не из Telegram. */
export function initData(): string {
  return tg?.initData ?? "";
}

export function tap(): void {
  tg?.HapticFeedback?.impactOccurred("light");
}

/** Спросить, можно ли поставить значок на главный экран и не стоит ли он уже.
 *
 *  Промис, а не колбэк: у Telegram тут колбэк, но экрану нужно одно значение
 *  один раз. Старый клиент метода не имеет — отвечаем `unsupported`, и кнопка
 *  просто не появляется: предлагать то, чего не произойдёт, хуже, чем молчать.
 */
export function homeScreenStatus(): Promise<HomeScreenStatus> {
  return new Promise((resolve) => {
    if (!tg?.checkHomeScreenStatus) return resolve("unsupported");
    // Если клиент не ответит, обещание висело бы вечно и кнопка не появилась бы
    // никогда — при молчании считаем, что поставить можно.
    const timer = setTimeout(() => resolve("missed"), 1500);
    tg.checkHomeScreenStatus((status) => {
      clearTimeout(timer);
      resolve(status);
    });
  });
}

/** Предложить поставить значок. Дальше решает Telegram и человек. */
export function addToHomeScreen(): void {
  tg?.addToHomeScreen?.();
}
